import { useMemo } from 'react';
import axiosInstance from './axiosConfig';

/**
 * Which Trade Ops desk to send someone back to when the screen doesn't know.
 *
 * Row 125 (Sept 2026): a recap that fails to load has no commodity to go by,
 * and a recap opened from a link rather than the Trade Ops table carries no
 * hint either. The screens fell back to the Cargo desk, which a bunkers-only
 * person may not open, so "Back to Trade Ops" bounced them to the dashboard.
 *
 * Same access reading as the navigation menu (HMSNavbar): full access opens
 * both desks, otherwise each desk needs its own tick-box. Returns a path; the
 * dashboard when the person can open neither desk.
 */
export const tradeOpsDeskFor = (user) => {
    const full = Boolean(user?.has_full_access || user?.is_superuser || user?.is_staff);
    if (full || user?.has_cargo_access) return '/dashboard/trades/solids';
    if (user?.has_bunkers_access) return '/dashboard/trades/fuels';
    return '/dashboard';
};

const asDesk = (value) => (value === 'solids' || value === 'fuels' ? value : null);

/**
 * THE rule for which desk a Trade Ops screen belongs to (row 127):
 *   1. the loaded record's own commodity_type, else
 *   2. the hint from where the person came (the Trade Ops table's
 *      location.state.commodityType, or a desk named in the address), else
 *   3. unknown: `known` is false and Back asks the server which desk this
 *      person can open (goToTradeOps).
 *
 * `commodity` is always a desk, 'solids' while unknown, because screens need a
 * form flavour from the first paint and a new record is still stamped with it.
 */
export const tradeOpsDesk = ({ recordCommodity, hintCommodity } = {}) => {
    const desk = asDesk(recordCommodity) || asDesk(hintCommodity);
    const commodity = desk || 'solids';
    return { commodity, basePath: `/dashboard/trades/${commodity}`, known: Boolean(desk) };
};

export const useTradeOpsDesk = ({ recordCommodity, hintCommodity } = {}) => useMemo(
    () => tradeOpsDesk({ recordCommodity, hintCommodity }),
    [recordCommodity, hintCommodity],
);

/**
 * The Trade Ops path for a screen: the desk it was opened from when that is
 * known, otherwise the desk this person can actually open (asks the server).
 */
export const resolveTradeOpsPath = async (knownCommodity) => {
    const desk = asDesk(knownCommodity);
    if (desk) {
        return `/dashboard/trades/${desk}`;
    }
    try {
        const response = await axiosInstance.get('/api/dashboard/user-info/');
        return tradeOpsDeskFor(response?.data);
    } catch (error) {
        console.error('Could not read desk access for Back to Trade Ops:', error);
        return '/dashboard';
    }
};

/**
 * Back to Trade Ops for a desk from tradeOpsDesk/useTradeOpsDesk. A known desk
 * is pushed at once (same tick as the click, as the screens always did); an
 * unknown one is looked up first. `state` is what the screen restores on the
 * Trade Ops table; commodityType is set to the desk actually opened.
 */
export const goToTradeOps = (history, desk, state = {}) => {
    const push = (path) => {
        history.push(path, { ...state, commodityType: path.endsWith('/fuels') ? 'fuels' : 'solids' });
        return path;
    };
    if (desk?.known) return Promise.resolve(push(desk.basePath));
    return resolveTradeOpsPath(null).then(push);
};
